import type { AnalyticsDto } from "@hisense/shared";
import { majorUnits } from "@/domain/money";
import type { CsvColumn } from "@/lib/exportCsv";

/**
 * Phân tích bán hàng ra MỘT tệp ở dạng "dài" (mỗi dòng một quan sát), không
 * phải năm tệp theo năm biểu đồ.
 *
 * Dạng dài là dạng Excel và Google Sheets xoay bảng (PivotTable) được ngay:
 * lọc theo cột Chiều là ra đúng biểu đồ đang xem, mà vẫn dán chung một sổ
 * được. Năm tệp rời thì mỗi lần muốn so hai chiều lại phải ghép tay.
 *
 * NÓI RÕ PHẦN BỊ CẮT: máy chủ chỉ trả TOP 8 cho sản phẩm và khách hàng, nên
 * nhãn chiều ghi đúng là "Top ..." — người đọc tệp không có màn hình bên cạnh
 * để đoán ra điều đó, và một tệp tưởng là đầy đủ thì nguy hiểm hơn một tệp
 * biết rõ mình thiếu gì.
 */
export interface AnalyticsCsvRow {
  dimension: string;
  label: string;
  revenue: number | null;
  quantity: number | null;
  orders: number | null;
  /**
   * Số ĐẾM (bao nhiêu sản phẩm khác nhau, bao nhiêu khách đang giao dịch) —
   * cột riêng, không dồn vào "Tổng số lượng". Đó là hai đại lượng khác nhau:
   * "7 sản phẩm" và "139 chiếc" mà nằm chung một cột thì ai cộng cột đó cũng
   * ra một con số vô nghĩa.
   */
  count: number | null;
}

type Translate = (key: string) => string;

export function buildAnalyticsRows(a: AnalyticsDto, t: Translate): AnalyticsCsvRow[] {
  const row = (
    dimension: string,
    label: string,
    revenue: number | null,
    quantity: number | null = null,
    orders: number | null = null,
    count: number | null = null,
  ): AnalyticsCsvRow => ({ dimension, label, revenue, quantity, orders, count });

  // Các ô tổng đứng trước: mở tệp ra là thấy ngay bối cảnh của mọi dòng dưới.
  const summary = t("analytics.summary");
  return [
    row(summary, t("analytics.totalBilled"), a.totalRevenue),
    row(summary, t("analytics.orders"), null, null, a.orderCount),
    row(summary, t("analytics.avgOrderValue"), a.averageOrderValue),
    row(summary, t("analytics.totalQuantity"), null, a.totalQuantity),
    row(summary, t("analytics.productsSold"), null, null, null, a.productCount),
    row(summary, t("analytics.activeCustomers"), null, null, null, a.customerCount),
    ...a.byMonth.map((m) => row(t("analytics.revenueByMonth"), m.month, m.revenue)),
    ...a.byProduct.map((p) => row(t("analytics.topProductsRevenue"), p.code, p.revenue, p.qty)),
    ...a.byCustomer.map((c) => row(t("analytics.topCustomersRevenue"), c.name, c.revenue)),
    ...a.byRespBy.map((r) => row(t("analytics.byRespBy"), r.name, r.revenue, null, r.orders)),
    ...a.byProductType.map((r) => row(t("analytics.byProductType"), r.type, r.revenue, r.qty)),
  ];
}

export function analyticsColumns(t: Translate): CsvColumn<AnalyticsCsvRow>[] {
  return [
    { header: t("analytics.dimension"), value: (r) => r.dimension },
    { header: t("common.name"), value: (r) => r.label },
    { header: t("analytics.totalBilled"), value: (r) => (r.revenue === null ? null : majorUnits(r.revenue)) },
    { header: t("analytics.totalQuantity"), value: (r) => r.quantity },
    { header: t("analytics.orders"), value: (r) => r.orders },
    { header: t("analytics.count"), value: (r) => r.count },
  ];
}
